import dayjs, { Dayjs } from 'dayjs';
import ReactECharts from 'echarts-for-react';
import { useContext, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import trapImageApi from '../../apis/TrapImageApi';
import CustomContext from '../../contexts/CustomContext';
import { CustomAuth, Plot, TrapPlot, TrapImageStats } from '../../models/Entities';
import alertService from '../../services/AlertService';
import dateService from '../../services/DateService';
import stringService from '../../services/StringService';
import styles from './ChartHumidityComponent.module.css';

/**
 * Returns the chart humidity component.
 * @returns the chart humidity component
 */
const ChartHumidityComponent: React.FC<Props> = (props) => {
    const { plot, trapPlot } = props;

    /*** HOOKS ***/

    const intl = useIntl();
    const [dataset, setDataset] = useState<(string | number | undefined)[][]>([]);
    const [series, setSeries] = useState<string[]>([]);
    const [dimensions, setDimensions] = useState<string[]>([]);
    const [, setLoading] = useState<'loading'>();
    const context = useContext(CustomContext);
    const auth = context.auth as CustomAuth;

    /*** EFFECTS ***/

    useEffect(() => {
        const init = async () => {
            try {
                setLoading('loading');

                const startDate: Dayjs = dayjs().subtract(100, 'days');
                const endDate: Dayjs = dayjs();

                let trapImageStats: TrapImageStats[] = [];
                if (auth.companyId && trapPlot && trapPlot.id) {
                    trapImageStats = await trapImageApi.listStats(startDate, endDate, 'DAY', auth.companyId, undefined, trapPlot.id);
                } else if (auth.companyId && plot && plot.id) {
                    trapImageStats = await trapImageApi.listStats(startDate, endDate, 'DAY', auth.companyId, plot.id);
                } else if (auth.companyId && !plot) {
                    trapImageStats = await trapImageApi.listStats(startDate, endDate, 'DAY', auth.companyId);
                }
                const { dimensions, series, dataset } = buildChartData(trapImageStats);

                setSeries(series);
                setDimensions(dimensions);
                setDataset(dataset);
            } catch (error) {
                alertService.displayError(error, intl);
            } finally {
                setLoading(undefined);
            }
        };
        init();
    }, [intl, auth.companyId, plot, trapPlot]);

    /*** METHODS ***/

    const buildChartData = (trapImageStats: TrapImageStats[]): any => {
        const trapPlots: TrapPlot[] = trapImageStats
            .map((tis) => tis.trapPlot)
            .filter((value, index, self) => index === self.findIndex((t) => t.id === value.id))
            .sort((a, b) => stringService.sort(a.trap?.uuid, b.trap?.uuid));
        const series: string[] = trapPlots.map((te) => `${te.trap?.uuid} - ${te.plot?.name}`);
        const dimensions: string[] = ['timestamp', ...series];

        const dates: Dayjs[] = trapImageStats
            .map((tis) => tis.date)
            .map((d) => d.startOf('day'))
            .filter((value, index, self) => index === self.findIndex((t) => t.isSame(value, 'day')))
            .sort(dateService.sort);
        const dataset: (string | number | undefined)[][] = dates.map((date) => {
            const values: (number | undefined)[] = trapPlots.flatMap((trapPlot) =>
                trapImageStats
                    .filter((tis) => tis.trapPlot.id === trapPlot.id && tis.date.isSame(date, 'day'))
                    .map((tis) => tis.avgHumidity)
                    .find(Boolean)
            );

            return [date.toISOString(), ...values];
        });

        return { series, dimensions, dataset };
    };

    /*** VISUAL ***/

    return (
        <div className={styles.container}>
            <ReactECharts
                option={{
                    legend: {
                        bottom: 0
                    },
                    grid: {
                        top: 10
                    },
                    dataset: {
                        source: dataset,
                        dimensions
                    },
                    xAxis: { type: 'time' },
                    yAxis: {},
                    series: series.map((s) => ({
                        name: s,
                        type: 'line',
                        connectNulls: true,
                        smooth: true,
                        encode: {
                            x: 'timestamp',
                            y: s
                        }
                    })),
                    tooltip: {
                        trigger: 'axis',
                        valueFormatter: (value: number | undefined) =>
                            value === undefined
                                ? ''
                                : intl.formatNumber(value, {
                                      minimumFractionDigits: 2,
                                      maximumFractionDigits: 2,
                                      unit: 'percent',
                                      unitDisplay: 'short',
                                      style: 'unit'
                                  }),
                        axisPointer: {
                            label: {
                                formatter: function (params: any) {
                                    return dayjs(params.value).format('DD/MM/YYYY');
                                }
                            }
                        }
                    }
                }}
            />
        </div>
    );
};
export default ChartHumidityComponent;

interface Props {
    plot?: Plot;
    trapPlot?: TrapPlot;
}
